import { apiRequest } from "./api";
import { isNativeMobileShell } from "./mobileBridge";

function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const bytes = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(bytes, (character) => character.charCodeAt(0));
}

export async function enableWebPush(): Promise<boolean> {
  if (isNativeMobileShell()) {
    const config = await apiRequest<{ nativeEnabled: boolean }>("/api/notifications/config");
    if (!config.nativeEnabled) return false;
    const nativeWindow = window as unknown as { __JTUC_NATIVE_PUSH_TOKEN__?: { provider: "fcm"; token: string } };
    const existing = nativeWindow.__JTUC_NATIVE_PUSH_TOKEN__;
    const subscription = existing ?? await new Promise<{ provider: "fcm"; token: string } | null>((resolve) => {
      const timeout = window.setTimeout(() => { window.removeEventListener("jtuc:native-push-token", receive as EventListener); resolve(null); }, 10_000);
      const receive = (event: CustomEvent<{ provider?: "fcm"; token?: string }>) => {
        if (!event.detail?.provider || !event.detail.token) return;
        window.clearTimeout(timeout); window.removeEventListener("jtuc:native-push-token", receive as EventListener);
        resolve({ provider: event.detail.provider, token: event.detail.token });
      };
      window.addEventListener("jtuc:native-push-token", receive as EventListener);
    });
    if (!subscription) return false;
    await apiRequest<void>("/api/notifications/native-subscription", { method: "POST", body: subscription });
    return true;
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return false;
  const config = await apiRequest<{ enabled: boolean; publicKey: string | null }>("/api/notifications/config");
  if (!config.enabled || !config.publicKey) return false;
  const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
  if (permission !== "granted") return false;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription() ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey(config.publicKey),
  });
  await apiRequest<void>("/api/notifications/subscription", { method: "POST", body: subscription.toJSON() });
  return true;
}

export async function disableWebPush(): Promise<void> {
  if (isNativeMobileShell()) {
    await apiRequest<void>("/api/notifications/native-subscription", { method: "DELETE" });
    return;
  }
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) await subscription.unsubscribe();
  await apiRequest<void>("/api/notifications/subscription", { method: "DELETE" });
}

export async function setApplicationBadge(count: number): Promise<void> {
  const appNavigator = navigator as Navigator & { clearAppBadge?: () => Promise<void>; setAppBadge?: (value?: number) => Promise<void> };
  if (count > 0) await appNavigator.setAppBadge?.(count);
  else await appNavigator.clearAppBadge?.();
}
